import { Box, Button, Center, Flex, Heading, Input, VStack, Text } from '@chakra-ui/react';
import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getToken, setToken } from '@bluefalcon/utils-package';
import { authService } from '../../services';
import { Tenant } from '@bluefalcon/users-management-client-fetch';
import { LoadingSpinner } from '../../components/base/loading-spinner/loading-spinner.comp';
import { BF_REFRESH, BF_SESSION_ID, BF_TOKEN, TENANT_NAME } from '../../constants/cookies.constants';
import { GoogleLogin, GoogleCredentialResponse } from '@react-oauth/google';
import { Dropdown } from '../../components/dropdown/dropdown.comp';
import { toast } from '../../utils/toast.utils';

export type LoginRequest = {
    username: string;
    password: string;
    tenantId?: string;
};

type LoginProps = {
    routeOnLogin?: string;
    headerTitle?: string;
};

export function Login({
    routeOnLogin,
    headerTitle,
}: LoginProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState<Tenant>();
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [pageState, setPageState] = useState<
        'multi_tenant' |
        'base_login' |
        'no_tenant'
    >('base_login');
    const [formData, setFormData] = useState<LoginRequest>({
        username: '',
        password: '',
    });
    const navigate = useNavigate();

    useEffect(() => {
        if (getToken(BF_TOKEN)) {
            // navigate('/logout');
        }
    }, [navigate]);

    const handleSubmit = async (e?: any) => {
        e?.preventDefault();
        try {
            setIsLoading(true);
            const { token, code, tenants: tenantsResponse, refresh, sessionId, userId } = await authService.login({ ...formData, tenantId: selectedTenant?.id });
            if (token) {
                setToken(BF_TOKEN, token);
                setToken(BF_REFRESH, String(refresh));
                setToken(BF_SESSION_ID, String(sessionId));
                selectedTenant && setToken(TENANT_NAME, selectedTenant.name);
                navigate(`${routeOnLogin ?? '/'}`);
                return;
            } else if (tenantsResponse && tenantsResponse.length > 0 && code === 'MULTI_TENANTS') {
                setPageState('multi_tenant');
                setTenants(tenantsResponse);
            }
            else if (code === 'NO_TENANT') {
                setPageState('no_tenant');
            }
        } catch (e: any) {
            setPageState('base_login')
            setTenants([]);
            setFormData((prevFormData) => ({ ...prevFormData, password: '' }));
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleTenantChange = (name?: string) => {
        const foundTenant = tenants.find((t) => t.name === name);
        setSelectedTenant(foundTenant);
    };

    const cancelMultiTenants = () => {
        setPageState('base_login');
        setTenants([]);
        setFormData((prevFormData) => ({ ...prevFormData, password: '' }));
        setSelectedTenant(undefined);
    };

    const handleGoogleLoginSuccess = async (credentialResponse: GoogleCredentialResponse) => {
        try {
            setIsLoading(true);
            if (!credentialResponse.credential) {
                return;
            }
            const {
                token,
                refresh,
                sessionId,
                tenants: tenantsResponse,
                code,
            } = await authService.googleLogin({ credential: credentialResponse.credential });
            if (token) {
                setToken(BF_TOKEN, token);
                setToken(BF_REFRESH, String(refresh));
                setToken(BF_SESSION_ID, String(sessionId));
                selectedTenant && setToken(TENANT_NAME, selectedTenant.name);
                navigate(`${routeOnLogin ?? '/'}`);
            }
            if (tenantsResponse && tenantsResponse.length > 0 && code === 'MULTI_TENANTS') {
                setPageState('multi_tenant');
                setTenants(tenantsResponse);
            }
            if (code === 'NO_TENANT') {
                setPageState('no_tenant');
            }
        } catch (err: any) {
            console.error('Google login failed', err);
            toast('Google Login Failed', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Flex direction="column" minH="100vh" bgGradient="linear(to-br, primary.200, primary.500)">
            <Box p={4} position="absolute" top={0} left={0}>
                <Text fontSize="2xl" fontWeight="bold" color="white">
                    {headerTitle ?? 'Falcon Admin'}
                </Text>
            </Box>
            <Center flex="1">
                {!isLoading ?
                    <Box
                        w="full"
                        maxW="lg"
                        p={8}
                        bg="white"
                        borderRadius="lg"
                        boxShadow="lg"
                    >
                        {pageState === 'no_tenant' && (
                            <Box>
                                <Heading as="h1" size="xl" mb={6} color="primary.500">
                                    No Tenant Found
                                </Heading>
                                <Box mt={4}>
                                    <Box p={4}>
                                        <Text>No Tenant Associated with this User</Text>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                        {pageState === 'base_login' &&
                            <Box>
                                <Heading as="h1" size="xl" mb={6} color="primary.500">
                                    Login
                                </Heading>
                                <Box mt={4}>
                                    <Box p={4}>
                                        <form onSubmit={handleSubmit}>
                                            <VStack spacing={4}>
                                                <Input
                                                    name="username"
                                                    placeholder="Username"
                                                    value={formData.username}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                <Input
                                                    name="password"
                                                    type="password"
                                                    placeholder="Password"
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                <Button
                                                    disabled={!formData.password.trim() || !formData.username.trim()}
                                                    type="submit"
                                                    colorScheme="primary"
                                                    w="full"
                                                >
                                                    Login
                                                </Button>
                                            </VStack>
                                        </form>
                                    </Box>
                                    <Flex justifyContent={'center'}>
                                        <GoogleLogin
                                            onSuccess={handleGoogleLoginSuccess}
                                            onError={() => console.error('Google login error')}
                                        />
                                    </Flex>
                                </Box>
                            </Box>}
                        {pageState === 'multi_tenant' &&
                            <Box>
                                <Heading as="h1" size="xl" mb={6} color="primary.500">
                                    Select Organization
                                </Heading>
                                <Box mt={4}>
                                    <Box p={4}>
                                        <Dropdown
                                            placeholder='Select Organization'
                                            required={true}
                                            items={tenants.map((t) => t.name)}
                                            onChange={handleTenantChange}
                                            name='tenant'
                                            value={selectedTenant?.name}
                                        />
                                    </Box>
                                </Box>
                                <Flex>
                                    <Button
                                        onClick={cancelMultiTenants}
                                        variant="outlined"
                                        w="full"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleSubmit}
                                        isDisabled={!selectedTenant}
                                        colorScheme="primary"
                                        w="full"
                                    >
                                        Login
                                    </Button>
                                </Flex>
                            </Box>}
                    </Box> : <LoadingSpinner />}
            </Center>
        </Flex>
    );
};
